import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { useState, useEffect, createContext, useContext, useCallback } from "react";
import { Toaster } from "./components/ui/sonner";
import LandingPage from "./pages/LandingPage";
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
import AuthCallback from "./pages/AuthCallback";
import Dashboard from "./pages/Dashboard";
import SearchPage from "./pages/SearchPage";
import BookingsPage from "./pages/BookingsPage";
import MessagesPage from "./pages/MessagesPage";
import ProfilePage from "./pages/ProfilePage";
import WorkerDetailPage from "./pages/WorkerDetailPage";
import BecomeWorkerPage from "./pages/BecomeWorkerPage";
import WorkerDashboard from "./pages/WorkerDashboard";
import TermsPage from "./pages/TermsPage";
import PrivacyPage from "./pages/PrivacyPage";
import ContactPage from "./pages/ContactPage";
import AdminPage from "./pages/AdminPage";
import WorkerSignupPage from "./pages/WorkerSignupPage";
import WorkerCreateAccountPage from "./pages/WorkerCreateAccountPage";
import WorkerHomePage from "./pages/WorkerHomePage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import WorkerJobsPage from "./pages/WorkerJobsPage";
import WorkerSchedulePage from "./pages/WorkerSchedulePage";
import WorkerProfilePage from "./pages/WorkerProfilePage";
import WorkerEarningsPage from "./pages/WorkerEarningsPage";
import VerifyEmailPage from "./pages/VerifyEmailPage";
import VerifyCodePage from "./pages/VerifyCodePage";
import InvoiceDetailPage from "./pages/InvoiceDetailPage";
import WorkerDocumentsPage from "./pages/WorkerDocumentsPage";
import ParticipantDocumentsPage from "./pages/ParticipantDocumentsPage";
import PostShiftPage from "./pages/PostShiftPage";
import WorkerShiftsPage from "./pages/WorkerShiftsPage";
import HowItWorksParticipantPage from "./pages/HowItWorksParticipantPage";
import HowItWorksWorkerPage from "./pages/HowItWorksWorkerPage";
import PricingPage from "./pages/PricingPage";
import SafetyPage from "./pages/SafetyPage";
import HelpCenterPage from "./pages/HelpCenterPage";
import ReportIncidentPage from "./pages/ReportIncidentPage";
import OnboardingPage from "./pages/OnboardingPage";
import RequestAdvisorPage from "./pages/RequestAdvisorPage";
import GetStartedPage from "./pages/GetStartedPage";
import WorkerPendingPage from "./pages/WorkerPendingPage";
import MarketingPage from "./pages/MarketingPage";
import FontComparePage from "./pages/FontComparePage";
import ServiceAgreementPage from "./pages/ServiceAgreementPage";
import PublicSignAgreementPage from "./pages/PublicSignAgreementPage";
import VerifyIdentityPage from "./pages/VerifyIdentityPage";
import { identify as analyticsIdentify, useTrackPageViews } from "./utils/analytics";
import "./App.css";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }

    try {
      // Check for pending user data from OAuth callback
      const pendingUserData = localStorage.getItem('pending_user_data');
      if (pendingUserData) {
        try {
          const userData = JSON.parse(pendingUserData);
          setUser(userData);
          localStorage.removeItem('pending_user_data'); // Clear it after use
          setLoading(false);
          return;
        } catch (e) {
          localStorage.removeItem('pending_user_data');
        }
      }

      const token = localStorage.getItem('auth_token');
      if (!token) {
        setLoading(false);
        return;
      }

      const response = await fetch(`${API}/auth/me`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (response.ok) {
        const userData = await response.json();
        setUser(userData);
      } else {
        // Token invalid, clear it
        localStorage.removeItem('auth_token');
      }
    } catch (error) {
      console.error("Auth check failed:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = (userData, token) => {
    // Ensure we only store clean, serializable primitive values
    // Explicitly convert everything to avoid any object reference issues on mobile
    const cleanUserData = {
      user_id: String(userData?.user_id || ""),
      email: String(userData?.email || ""),
      name: String(userData?.name || ""),
      user_type: String(userData?.user_type || "participant"),
      picture: userData?.picture ? String(userData.picture) : null,
      phone: userData?.phone || null,
      ndis_number: userData?.ndis_number || null,
      location: userData?.location || null,
      ndis_plan_type: userData?.ndis_plan_type || null,
      support_needs: userData?.support_needs || [],
      email_verified: Boolean(userData?.email_verified),
      participant_verified: Boolean(userData?.participant_verified)
    };
    setUser(cleanUserData);
    if (token) {
      localStorage.setItem('auth_token', String(token));
    }
  };

  const refreshUser = useCallback(async () => {
    await checkAuth();
  }, [checkAuth]);

  const logout = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      await fetch(`${API}/auth/logout`, {
        method: 'POST',
        headers: token ? { 'Authorization': `Bearer ${token}` } : {}
      });
    } catch (error) {
      console.error("Logout error:", error);
    }
    setUser(null);
    localStorage.removeItem('auth_token');
  };

  return (
    <AuthContext.Provider value={{ user, login, logout, loading, checkAuth, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
};

// Protected Route
const ProtectedRoute = ({ children, requireApprovedWorker = false }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#87CEEB]">
        <div className="flex flex-col items-center gap-4">
          <img 
            src="https://customer-assets.emergentagent.com/job_emergent-website-3/artifacts/6i1oj5oc_BTR_Health_favicon__2_-removebg-preview.png"
            alt="Loading"
            className="w-16 h-16 animate-spin"
            style={{ animation: 'spin 1.5s linear infinite' }}
          />
          <p className="text-white font-medium">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/btr-staff-portal" state={{ from: location }} replace />;
  }

  // Gate worker-only pages until admin approves the account.
  // /worker-signup, /onboarding and /worker-pending are deliberately reachable
  // so the worker can complete their profile and see status.
  if (
    requireApprovedWorker &&
    user.user_type === "support_worker" &&
    user.approval_status &&
    user.approval_status !== "approved"
  ) {
    return <Navigate to="/worker-pending" replace />;
  }

  return children;
};

// Smart Dashboard Route - redirects based on user type
const SmartDashboardRoute = () => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#87CEEB]">
        <div className="flex flex-col items-center gap-4">
          <img 
            src="https://customer-assets.emergentagent.com/job_emergent-website-3/artifacts/6i1oj5oc_BTR_Health_favicon__2_-removebg-preview.png"
            alt="Loading"
            className="w-16 h-16 animate-spin"
            style={{ animation: 'spin 1.5s linear infinite' }}
          />
          <p className="text-white font-medium">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/btr-staff-portal" state={{ from: location }} replace />;
  }

  // Support workers:
  //  • pending  → /worker-home (the new 3-screen flow's checklist + setup)
  //  • submitted / verified → /worker-pending (waiting-on-admin view)
  //  • approved → /worker-dashboard
  if (user.user_type === "support_worker") {
    const status = user.approval_status;
    if (!status || status === "pending") return <Navigate to="/worker-home" replace />;
    if (status === "approved" || status === "active") return <Navigate to="/worker-dashboard" replace />;
    return <Navigate to="/worker-pending" replace />;
  }

  // Participants go to regular dashboard
  return <Dashboard />;
};

// App Router
const AppRouter = () => {
  const location = useLocation();
  const { user } = useAuth();

  // Auto-fire "page_view" on every route change (first-party analytics).
  useTrackPageViews();

  // Keep analytics util's user_id in sync with auth state.
  useEffect(() => {
    analyticsIdentify(user?.user_id || null);
  }, [user?.user_id]);

  // Check URL fragment for session_id - must be synchronous during render
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      {/* Public pages */}
      <Route path="/" element={<LandingPage />} />
      <Route path="/search" element={<SearchPage />} />
      <Route path="/worker/:workerId" element={<WorkerDetailPage />} />
      <Route path="/become-worker" element={<BecomeWorkerPage />} />
      <Route path="/worker-signup" element={<WorkerSignupPage />} />
      <Route path="/worker-signup/create" element={<WorkerCreateAccountPage />} />
      <Route path="/how-it-works/participants" element={<HowItWorksParticipantPage />} />
      <Route path="/how-it-works/workers" element={<HowItWorksWorkerPage />} />
      <Route path="/pricing" element={<PricingPage />} />
      <Route path="/safety" element={<SafetyPage />} />
      <Route path="/help" element={<HelpCenterPage />} />
      <Route path="/contact" element={<ContactPage />} />
      <Route path="/terms" element={<TermsPage />} />
      <Route path="/privacy" element={<PrivacyPage />} />
      <Route path="/marketing" element={<MarketingPage />} />
      <Route path="/font-compare" element={<FontComparePage />} />
      <Route path="/request-advisor" element={<RequestAdvisorPage />} />
      <Route path="/sign-agreement/:token" element={<PublicSignAgreementPage />} />

      {/* Admin backdoor: /btr-staff-portal lets internal staff sign in, /admin is the console */}
      <Route path="/btr-staff-portal" element={<LoginPage />} />
      <Route path="/login" element={<Navigate to="/" replace />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/admin" element={<ProtectedRoute><AdminPage /></ProtectedRoute>} />

      {/* Redirect all deprecated auth / dashboard / worker-portal routes back home */}
      <Route path="/register" element={<Navigate to="/" replace />} />
      <Route path="/forgot-password" element={<Navigate to="/" replace />} />
      <Route path="/reset-password" element={<Navigate to="/" replace />} />
      <Route path="/get-started" element={<Navigate to="/" replace />} />
      <Route path="/dashboard" element={<Navigate to="/" replace />} />
      <Route path="/profile" element={<Navigate to="/" replace />} />
      <Route path="/bookings" element={<Navigate to="/" replace />} />
      <Route path="/messages" element={<Navigate to="/" replace />} />
      <Route path="/onboarding" element={<Navigate to="/" replace />} />
      <Route path="/verify-email/:token" element={<Navigate to="/" replace />} />
      <Route path="/verify-code" element={<Navigate to="/" replace />} />
      <Route path="/verify-identity" element={<Navigate to="/" replace />} />
      <Route path="/service-agreement" element={<Navigate to="/" replace />} />
      <Route path="/my-documents" element={<Navigate to="/" replace />} />
      <Route path="/report-incident" element={<Navigate to="/" replace />} />
      <Route path="/invoices/:invoiceId" element={<Navigate to="/" replace />} />
      <Route path="/post-shift" element={<Navigate to="/" replace />} />
      <Route path="/worker-home" element={<Navigate to="/worker-signup" replace />} />
      <Route path="/worker-pending" element={<Navigate to="/worker-signup" replace />} />
      <Route path="/worker-dashboard" element={<Navigate to="/worker-signup" replace />} />
      <Route path="/worker-jobs" element={<Navigate to="/worker-signup" replace />} />
      <Route path="/worker-schedule" element={<Navigate to="/worker-signup" replace />} />
      <Route path="/worker-profile" element={<Navigate to="/worker-signup" replace />} />
      <Route path="/worker-earnings" element={<Navigate to="/worker-signup" replace />} />
      <Route path="/worker-documents" element={<Navigate to="/worker-signup" replace />} />
      <Route path="/worker-shifts" element={<Navigate to="/worker-signup" replace />} />
    </Routes>
  );
};

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRouter />
        <Toaster position="top-center" richColors />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
